/**
 * Purchase form — multi-item. On initial save each item becomes a fresh
 * sealed or card lot tagged with Source ID = PUR-…
 */
import { DB } from "../lib/db";
import { CATEGORY_TO_GAME, resolveCatalogProduct, getCatalogPrice } from "../lib/catalogResolve";
import { escapeHtml, toast, scrollItemsToBottom, $ } from "../lib/dom";
import { fmt$, fmtN, fmtDate, todayISO } from "../utils/format";
import { GAMES } from "../utils/pills";
import { openModal, closeModal, markModalDirty } from "../components/modal";
import { formField, readForm } from "../components/formField";
import { wireCombobox } from "../components/combobox";
import { SetCache } from "../lib/sets";
import { nextId } from "../utils/ids";
import { render } from "../state";
import { requestEnrichment } from "../lib/enrich";
import { BULK_CLASSES, bulkPoolLotId, bulkUnitValueFor, addToBulkPool, type BulkClass } from "../lib/bulk";
import { quickCardLookup } from "../components/quickCardLookup";
import { openSealedPicker } from "../components/sealedProductPicker";
import { mileageSectionHtml, wireMileageSection, saveMileageSection } from "../components/mileageSection";
import { normalizeSetName } from "../lib/setNames";
import { processReceiptFile } from "../lib/receipt";
import { uploadReceiptDataUrl, deleteReceiptByPath, isStoragePath } from "../lib/storage";
import { viewReceipt } from "../components/receiptViewer";
import "../lib/collectrImport";
import { icon } from "../utils/icon";

type PurchaseItemType = "Raw" | "Graded" | "Sealed" | BulkClass;

function isBulkItemType(t: string | undefined): t is BulkClass {
  return t === "Bulk" || t === "Holo Bulk" || t === "Premium Bulk" || t === "$1-$5 Bulk" || t === "$5-$10 Bulk";
}

interface PurchaseItem {
  "Type": PurchaseItemType;
  "Game": string;
  "Item": string;
  "Set": string;
  "Number": string;
  "Condition": string;
  "Grade": string;
  "ProductType": string;
  "Qty": number;
  "UnitCost": number;
  "Lot ID"?: string;
  /** Transient: cached catalog market unit price for distribute-by-market mode.
   *  undefined = not looked up yet, null = looked up, no unique match/price.
   *  Stripped before the purchase row is saved. */
  "_mktUnit"?: number | null;
}
interface PurchaseState {
  "Purchase ID": string; "Date": string; "Vendor": string;
  "Payment Method": string; "Fees": number; "Notes": string;
  "Items": PurchaseItem[];
  "_isNew": boolean;
  /** Distribute-total-cost mode (form-time convenience; never persisted). */
  "_distOn"?: boolean;
  "_distTotal"?: number;
  "_distMode"?: "equal" | "market";
}
function S(): PurchaseState | null { return (window as any)._purchaseState as PurchaseState | null; }

/** In-form receipt UI (mirrors Expenses): upload / view / remove buttons. */
function renderPurchaseReceiptUI(row: any): string {
  const has = !!row["Receipt File"];
  const name = row["Receipt File Name"] || "";
  return `
    <button type="button" class="btn btn-ghost" onclick="document.getElementById('purchaseReceiptInput').click()">\u{1F4CE} ${has ? 'Replace receipt' : 'Upload receipt'}</button>
    ${has ? `
      <button type="button" class="btn btn-ghost" onclick="viewPurchaseReceiptFromForm()">\u{1F441} View</button>
      <button type="button" class="btn btn-ghost" onclick="clearPurchaseReceipt()">\u2715 Remove</button>
      <span class="text-xs text-[var(--muted)] truncate" style="max-width:240px">${escapeHtml(name || 'attached')}</span>
    ` : `<span class="text-xs text-[var(--muted)]">No receipt attached</span>`}`;
}

export function editPurchase(id?: string): void {
  const isNew = !id;
  const purchase = isNew
    ? { "Purchase ID": nextId("PUR", "purchases", "Purchase ID"), "Date": todayISO(),
        "Vendor": "", "Payment Method": "Cash", "Items": [], "Fees": 0, "Notes": "" }
    : DB.table("purchases").find(r => r["Purchase ID"] === id);
  if (!purchase) { toast("Purchase not found", "err"); return; }

  (window as any)._purchaseState = {
    "Purchase ID": purchase["Purchase ID"],
    "Date":        purchase["Date"],
    "Vendor":      purchase["Vendor"] || "",
    "Payment Method": purchase["Payment Method"] || "Cash",
    "Fees":        Number(purchase["Fees"]) || 0,
    "Notes":       purchase["Notes"] || "",
    "Items":       (purchase.Items || []).map((x: any) => ({ ...x })),
    "_isNew":      isNew,
  } as PurchaseState;

  const body = `
    <form id="purchaseForm" onsubmit="return false;">
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        ${formField({ label: "Purchase ID", name: "Purchase ID", value: purchase["Purchase ID"], required: true })}
        ${formField({ label: "Date", name: "Date", type: "date", value: fmtDate(purchase["Date"]), required: true })}
        ${formField({ label: "Vendor", name: "Vendor", value: purchase["Vendor"] || "", placeholder: "e.g. Local Game Store, eBay, WhatNot" })}
        ${formField({ label: "Payment Method", name: "Payment Method", value: purchase["Payment Method"] || "Cash", options: ["Cash", "Credit", "Debit", "ACH", "PayPal", "Venmo", "Cash App", "Other"] })}
      </div>

      <div class="mb-6">
        <div class="flex items-center justify-between mb-3 pb-2 border-b border-[var(--border)] flex-wrap gap-2">
          <h4 class="font-semibold text-emerald-300">Items (each becomes a new lot on save)</h4>
          <div class="flex gap-2">
            <button type="button" class="btn btn-ghost !py-1 !px-3 text-xs" onclick="triggerCollectrImport('purchase')" title="Import items from a Collectr portfolio CSV export. Items appear in the list below for review - nothing saves until you click Save.">${icon("upload")} Import CSV</button>
            <button type="button" class="btn btn-primary !py-1 !px-3 text-xs" onclick="addPurchaseItem()">+ Add Item</button>
          </div>
        </div>
        <div class="text-xs text-[var(--muted)] mb-2">Pick a Type per row: <b>Raw card</b> creates a Card lot (Inventory Class = Raw). <b>Graded card</b> creates a Card lot (Inventory Class = Slab) with the grade. <b>Sealed product</b> creates a Sealed lot.${!isNew ? " <span class='text-amber-300'>Editing won't re-apply inventory changes.</span>" : ""}</div>
        <div id="purchaseItems" class="space-y-2 max-h-[40vh] overflow-y-auto pr-1"></div>
        <div class="panel p-2 mt-2">
          <label class="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" id="purchDistOn" onchange="togglePurchDistribute(this.checked)">
            <span>Distribute one total cost across the items (instead of entering each cost)</span>
          </label>
          <div id="purchDistControls" class="hidden mt-2">
            <div class="grid grid-cols-12 gap-2 items-end">
              <div class="col-span-6 sm:col-span-3">
                <label class="label text-xs">Total cost of items ($)</label>
                <input class="input" type="number" step="0.01" min="0" id="purchDistTotal" oninput="setPurchDistTotal(this.value)">
              </div>
              <div class="col-span-6 sm:col-span-3">
                <label class="label text-xs">Split</label>
                <select class="select" id="purchDistMode" onchange="setPurchDistMode(this.value)">
                  <option value="equal">Equally per card/unit</option>
                  <option value="market">By market value</option>
                </select>
              </div>
              <div id="purchDistStatus" class="col-span-12 sm:col-span-6 text-xs text-[var(--muted)]"></div>
              <div id="purchDistNudge" class="hidden col-span-12 text-xs text-amber-300"></div>
            </div>
            <div class="text-xs text-[var(--muted)] mt-1">Unit costs below are computed automatically. Bulk rows are excluded and keep their own cost. Fees/Tax/Shipping still capitalize on top, pro-rata. Tip: for mixed lots with one big hit, "By market value" gives the most tax-accurate basis.</div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
        ${formField({ label: "Fees / Tax / Shipping ($)", name: "Fees", type: "number", value: purchase["Fees"] || 0, hint: "Capitalized into the items' cost basis pro-rata (freight-in) \u2014 flows to COGS when they sell." })}
        <div>${formField({ label: "Notes", name: "Notes", type: "textarea", value: purchase["Notes"] || "" })}</div>
      </div>

      <div class="mt-4 pt-3 border-t border-[var(--border)]">
        <label class="label">Receipt file (image or PDF)</label>
        <input type="hidden" name="Receipt File"      value="${escapeHtml(purchase["Receipt File"] || "")}">
        <input type="hidden" name="Receipt File Name" value="${escapeHtml(purchase["Receipt File Name"] || "")}">
        <input type="hidden" name="Receipt File Type" value="${escapeHtml(purchase["Receipt File Type"] || "")}">
        <input type="file" id="purchaseReceiptInput" accept="image/*,application/pdf" class="hidden" onchange="onPurchaseReceiptUpload(event)">
        <div id="purchaseReceiptUI" class="flex flex-wrap gap-2 items-center">${renderPurchaseReceiptUI(purchase)}</div>
        <div class="text-xs text-[var(--muted)] mt-2">One receipt per purchase. Images are auto-compressed before upload; files are stored privately in your Supabase Storage bucket.</div>
      </div>

      ${mileageSectionHtml(purchase["Purchase ID"], "Individual Purchase")}

      <div class="mt-4 pt-3 pb-3 -mx-4 px-4 border-t border-[var(--border)] grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm sticky bottom-[68px] z-10 bg-[var(--panel)] shadow-[0_-8px_12px_-12px_rgba(0,0,0,0.6)]">
        <div><div class="text-xs text-[var(--muted)]">Subtotal (items)</div><div id="purchSubtotal" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Fees</div><div id="purchFeesDisplay" class="stat-num font-bold">$0.00</div></div>
        <div><div class="text-xs text-[var(--muted)]">Total Paid</div><div id="purchTotal" class="stat-num font-bold">$0.00</div></div>
      </div>
    </form>`;
  const safeId = escapeHtml(purchase["Purchase ID"]);
  const footer = `
    ${!isNew ? `<button class="btn btn-danger" onclick="deleteRow('purchases','Purchase ID','${safeId}')">Delete</button>` : ""}
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" onclick="savePurchase('${isNew ? "" : safeId}')">${isNew ? "Log" : "Save"}</button>`;
  openModal({ title: isNew ? "Add Purchase" : "Edit Purchase", body, footer, wide: true });

  const feesInput = document.querySelector<HTMLInputElement>('#purchaseForm [name="Fees"]');
  feesInput?.addEventListener("input", () => {
    const s = S(); if (!s) return;
    s.Fees = Number(feesInput.value) || 0;
    recomputePurchaseTotals();
  });
  renderPurchaseItemsList();
  wireMileageSection();
}

export function addPurchaseItem(type?: PurchaseItemType): void {
  const s = S(); if (!s) return;
  s.Items.push({
    "Type": (type || "Raw"),
    "Game": "Pokemon",
    "Item": "", "Set": "", "Number": "",
    "Condition": "Near Mint", "Grade": "PSA 10",
    "ProductType": "", "Qty": 1, "UnitCost": 0,
  });
  if (s._distOn) { applyPurchaseDistribution(); scheduleDistResolve(); }
  renderPurchaseItemsList();
  markModalDirty();
  scrollItemsToBottom("purchaseItems");
}
export function removePurchaseItem(idx: number): void {
  const s = S(); if (!s) return;
  s.Items.splice(idx, 1);
  if (s._distOn) { applyPurchaseDistribution(); scheduleDistResolve(); }
  renderPurchaseItemsList();
  markModalDirty();
}
export function updatePurchaseItemField(idx: number, field: string, value: string): void {
  const s = S(); if (!s) return;
  const it = s.Items[idx]; if (!it) return;
  (it as any)[field] = (field === "Qty" || field === "UnitCost") ? (Number(value) || 0) : value;
  if (s._distOn && field !== "UnitCost") {
    if (field === "Item" || field === "Set" || field === "Number" || field === "Game") { delete it._mktUnit; scheduleDistResolve(); }
    applyPurchaseDistribution();
    if (field !== "Type") refreshDistCostInputs();
  }
  recomputePurchaseTotals();
  markModalDirty();
  if (field === "Type") renderPurchaseItemsList();
}

function renderPurchaseItemsList(): void {
  const container = document.getElementById("purchaseItems");
  if (!container) return;
  const s = S(); if (!s) return;
  const items = s.Items;
  const distOn = !!s._distOn;
  document.getElementById("purchaseItemsAddMore")?.classList.toggle("hidden", !items.length);
  if (!items.length) {
    container.innerHTML = `<div class="text-sm text-[var(--muted)] italic py-2">No items yet — click "+ Add Item".</div>`;
    recomputePurchaseTotals();
    return;
  }
  container.innerHTML = items.map((item, i) => {
    const t = (item.Type || "Raw") as PurchaseItemType;
    const isBulk = isBulkItemType(t);
    const isCard = (t === "Raw" || t === "Graded");
    const typeBadgeColor = isBulk
      ? (t === "Bulk" ? "gray" : (t === "Holo Bulk" ? "violet" : "amber"))
      : (t === "Sealed" ? "blue" : (t === "Graded" ? "amber" : "violet"));
    const lineTotal = isBulk ? (Number(item.UnitCost) || 0) : (Number(item.Qty) || 0) * (Number(item.UnitCost) || 0);

    // Type dropdown + game selector header — shared across all row variants
    const headerRow = `
      <div class="flex items-center gap-2 mb-2 flex-wrap">
        <span class="pill pill-${typeBadgeColor}">${escapeHtml(t)}</span>
        <select class="select !py-1 text-sm w-auto" onchange="updatePurchaseItemField(${i},'Type',this.value)">
          <option value="Raw"    ${t === "Raw" ? "selected" : ""}>Raw card</option>
          <option value="Graded" ${t === "Graded" ? "selected" : ""}>Graded card</option>
          <option value="Sealed" ${t === "Sealed" ? "selected" : ""}>Sealed product</option>
          ${BULK_CLASSES.map(k => `<option value="${escapeHtml(k)}" ${t === k ? "selected" : ""}>${escapeHtml(k)}</option>`).join("")}
        </select>
        ${isBulk ? "" : `<select class="select !py-1 text-sm w-auto" onchange="updatePurchaseItemField(${i},'Game',this.value)">
          ${GAMES.map(g => `<option value="${escapeHtml(g)}" ${item.Game === g ? "selected" : ""}>${escapeHtml(g)}</option>`).join("")}
        </select>`}
        <span class="ml-auto text-xs text-[var(--muted)]">Line total: <b class="text-emerald-300" data-line-total>${fmt$(lineTotal)}</b></span>
        ${!isBulk ? `<button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="${isCard ? `lookupForPurchaseItem(${i})` : `lookupSealedForPurchaseItem(${i})`}" title="Find on ${isCard ? "the Pokémon TCG API" : "the sealed product catalog"}">⌕ Lookup</button>` : ""}
        <button type="button" class="btn btn-ghost !py-1 !px-2 text-xs" onclick="removePurchaseItem(${i})" title="Remove">✕</button>
      </div>`;

    // Bulk rows collapse to just Qty + Unit Cost. Pool reference info is included as muted text.
    if (isBulk) {
      const poolUnitValue = bulkUnitValueFor(t as BulkClass);
      return `
        <div class="panel p-2" data-purch-idx="${i}">
          ${headerRow}
          <div class="grid grid-cols-12 gap-2 items-end">
            <div class="col-span-3 sm:col-span-2">
              <label class="label text-xs">Qty</label>
              <input class="input" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updatePurchaseItemField(${i},'Qty',this.value)">
            </div>
            <div class="col-span-9 sm:col-span-3">
              <label class="label text-xs">Total cost ($)</label>
              <input class="input" type="number" step="0.01" value="${item.UnitCost || 0}" oninput="updatePurchaseItemField(${i},'UnitCost',this.value)">
            </div>
            <div class="col-span-12 sm:col-span-7 text-xs text-[var(--muted)]">
              Adds ${fmtN(Number(item.Qty) || 0)} cards to the <b>${escapeHtml(t)}</b> pool at a cost of <b>${fmt$(Number(item.UnitCost) || 0)}</b> (avg <b>${fmt$((Number(item.Qty) || 0) > 0 ? (Number(item.UnitCost) || 0) / (Number(item.Qty) || 1) : 0)}/card</b>). Pool market value stays <b>${fmt$(poolUnitValue)}/card</b> from Configs.${distOn ? ` <b>Excluded from the distributed total</b> \u2014 enter this row's cost here.` : ""}
            </div>
          </div>
        </div>`;
    }

    // Singles / sealed — original layout
    const detailField = !isCard
      ? `<div class="col-span-12 sm:col-span-2"><label class="label text-xs">Product Type</label><input class="input" placeholder="e.g. ETB" value="${escapeHtml(item.ProductType || "")}" oninput="updatePurchaseItemField(${i},'ProductType',this.value)"></div>`
      : `<div class="col-span-6 sm:col-span-1"><label class="label text-xs">Card #</label><input class="input" placeholder="#" value="${escapeHtml(item.Number || "")}" oninput="updatePurchaseItemField(${i},'Number',this.value)"></div>
         ${t === "Graded"
            ? `<div class="col-span-6 sm:col-span-2"><label class="label text-xs">Grade</label><select class="select" onchange="updatePurchaseItemField(${i},'Grade',this.value)">${["PSA 10","PSA 9","PSA 8","BGS 10","BGS 9.5","BGS 9","CGC 10","CGC 9.5","CGC 9","SGC 10","Pending","Other"].map(o => `<option ${item.Grade === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}</select></div>`
            : `<div class="col-span-6 sm:col-span-2"><label class="label text-xs">Condition</label><select class="select" onchange="updatePurchaseItemField(${i},'Condition',this.value)">${["Near Mint","Mint","Lightly Played","Moderately Played","Heavily Played","Damaged","Other"].map(o => `<option ${item.Condition === o ? "selected" : ""}>${escapeHtml(o)}</option>`).join("")}</select></div>`
         }`;
    return `
      <div class="panel p-2" data-purch-idx="${i}">
        ${headerRow}
        <div class="grid grid-cols-12 gap-2">
          <div class="col-span-12 sm:col-span-5">
            <label class="label text-xs">${isCard ? "Card Name" : "Product Name"}</label>
            <input class="input" placeholder="${isCard ? 'e.g. Charizard ex' : 'e.g. Surging Sparks Booster Box'}"
                   value="${escapeHtml(item.Item || "")}"
                   oninput="updatePurchaseItemField(${i},'Item',this.value)">
          </div>
          <div class="col-span-12 sm:col-span-3">
            <label class="label text-xs">Set</label>
            <div class="combobox-wrapper" data-purch-set="${i}">
              <input class="input" autocomplete="off" placeholder="Type to search…"
                     value="${escapeHtml(item.Set || "")}"
                     oninput="updatePurchaseItemField(${i},'Set',this.value)">
              <div class="combobox-list"></div>
            </div>
          </div>
          ${detailField}
          <div class="col-span-3 sm:col-span-1">
            <label class="label text-xs">Qty</label>
            <input class="input" type="number" step="1" min="1" value="${item.Qty || 1}" oninput="updatePurchaseItemField(${i},'Qty',this.value)">
          </div>
          <div class="col-span-9 sm:col-span-2">
            <label class="label text-xs">Unit Cost ($)${distOn ? ' <span class="text-[var(--muted)]">\u00b7 auto</span>' : ""}</label>
            <input class="input" type="number" step="0.01" data-dist-cost value="${item.UnitCost || 0}" ${distOn ? 'readonly tabindex="-1"' : ""} oninput="updatePurchaseItemField(${i},'UnitCost',this.value)">
          </div>
        </div>
      </div>`;
  }).join("");
  // Per-row Set combobox follows the row's Game (singles/sealed only — bulk rows have no set field)
  items.forEach((item, i) => {
    if (isBulkItemType(item.Type)) return;
    const wrapper = container.querySelector<HTMLElement>(`[data-purch-set="${i}"]`);
    if (!wrapper) return;
    const getGame = () => {
      const cur = S();
      return (cur?.Items[i]?.Game) || "Pokemon";
    };
    wireCombobox(wrapper, {
      getOptions: () => SetCache.get(getGame()),
      asyncLoader: () => SetCache.ensureFresh(getGame()),
      onSelect: (v) => updatePurchaseItemField(i, "Set", v),
    });
  });
  recomputePurchaseTotals();
  updateDistStatus();
}

function recomputePurchaseTotals(): void {
  const s = S(); if (!s) return;
  const items = s.Items || [];
  const subtotal = items.reduce((acc, x) => acc + (isBulkItemType(x.Type) ? (Number(x.UnitCost) || 0) : (Number(x.Qty) || 0) * (Number(x.UnitCost) || 0)), 0);
  const fees = Number(s.Fees) || 0;
  const total = subtotal + fees;
  const setText = (id: string, val: string) => { const el = document.getElementById(id); if (el) el.textContent = val; };
  setText("purchSubtotal",     fmt$(subtotal));
  setText("purchFeesDisplay",  fmt$(fees));
  setText("purchTotal",        fmt$(total));
}

/* ============================================================
   Distribute-total-cost mode (2026-07-14).
   Form-time convenience: one entered total is spread across the
   non-bulk items, either equally per card/unit or weighted by
   catalog market value (equal-average fallback for unpriced
   rows). Bulk rows keep their own entered cost. Nothing here is
   persisted \u2014 savePurchase sees ordinary per-item Unit Costs, so
   fee capitalization and lot creation are untouched.
   ============================================================ */

function distEligible(items: PurchaseItem[]): PurchaseItem[] {
  return items.filter(x => !isBulkItemType(x.Type) && (Number(x.Qty) || 0) > 0);
}

/** Recompute each eligible item's Unit Cost from the single entered total.
 *  Synchronous \u2014 market mode uses whatever prices are already cached on the
 *  rows (resolveDistPrices fills the cache). Penny-exact: line totals are
 *  rounded per row and the remainder parks on the last row. */
function applyPurchaseDistribution(itemsOverride?: PurchaseItem[]): void {
  const s = S(); if (!s || !s._distOn) return;
  const total = Number(s._distTotal) || 0;
  const eligible = distEligible(itemsOverride || s.Items || []);
  if (!eligible.length || total <= 0) { updateDistStatus(); return; }

  let weights: number[];
  if (s._distMode === "market") {
    const priced = eligible.filter(x => typeof x._mktUnit === "number" && x._mktUnit > 0);
    const avg = priced.length
      ? priced.reduce((a, x) => a + (x._mktUnit as number), 0) / priced.length
      : 1; // nothing priced yet -> degenerates to an equal split
    weights = eligible.map(x =>
      (Number(x.Qty) || 0) * ((typeof x._mktUnit === "number" && x._mktUnit > 0) ? x._mktUnit : avg));
  } else {
    weights = eligible.map(x => Number(x.Qty) || 0);
  }
  const wSum = weights.reduce((a, b) => a + b, 0);
  if (wSum <= 0) { updateDistStatus(); return; }

  let allocated = 0;
  eligible.forEach((it, i) => {
    const qty = Number(it.Qty) || 1;
    const line = (i === eligible.length - 1)
      ? +(total - allocated).toFixed(2)
      : +((total * weights[i]!) / wSum).toFixed(2);
    allocated = +(allocated + line).toFixed(2);
    it.UnitCost = qty > 0 ? +(line / qty).toFixed(4) : line;
  });
  updateDistStatus();
}

function updateDistStatus(): void {
  updateDistNudge();
  const el = document.getElementById("purchDistStatus");
  const s = S();
  if (!el) return;
  if (!s || !s._distOn) { el.textContent = ""; return; }
  const eligible = distEligible(s.Items || []);
  if (!eligible.length) { el.textContent = "No card/sealed rows to distribute across yet."; return; }
  if (s._distMode === "market") {
    const priced = eligible.filter(x => typeof x._mktUnit === "number" && x._mktUnit > 0).length;
    el.textContent = `${priced} of ${eligible.length} row${eligible.length === 1 ? "" : "s"} priced from the catalog` +
      (priced < eligible.length ? " \u2014 unpriced rows get an average-value share." : ".");
  } else {
    const units = eligible.reduce((a, x) => a + (Number(x.Qty) || 0), 0);
    const total = Number(s._distTotal) || 0;
    el.textContent = `${fmt$(total)} across ${fmtN(units)} card${units === 1 ? "" : "s"}/units \u2248 ${fmt$(units > 0 ? total / units : 0)} each.`;
  }
}

/** Equal-mode nudge: prices resolve quietly in the background even in equal
 *  mode; when the resolved market values show an equal split would put a
 *  meaningful amount of basis on the wrong rows (>= $10 AND >= 10% of the
 *  total), suggest switching to the market-value split with one click. */
function updateDistNudge(): void {
  const el = document.getElementById("purchDistNudge");
  const s = S(); if (!el) return;
  const hide = () => { el.classList.add("hidden"); el.innerHTML = ""; };
  if (!s || !s._distOn || s._distMode === "market") { hide(); return; }
  const total = Number(s._distTotal) || 0;
  const eligible = distEligible(s.Items || []);
  const priced = eligible.filter(x => typeof x._mktUnit === "number" && x._mktUnit > 0);
  if (total <= 0 || eligible.length < 2 || priced.length < 2) { hide(); return; }
  const avg = priced.reduce((a, x) => a + (x._mktUnit as number), 0) / priced.length;
  const mktW = eligible.map(x =>
    (Number(x.Qty) || 0) * ((typeof x._mktUnit === "number" && x._mktUnit > 0) ? x._mktUnit : avg));
  const eqW = eligible.map(x => Number(x.Qty) || 0);
  const mSum = mktW.reduce((a, b) => a + b, 0);
  const eSum = eqW.reduce((a, b) => a + b, 0);
  if (mSum <= 0 || eSum <= 0) { hide(); return; }
  let maxDiff = 0;
  eligible.forEach((_x, i) => {
    const d = Math.abs((total * mktW[i]!) / mSum - (total * eqW[i]!) / eSum);
    if (d > maxDiff) maxDiff = d;
  });
  if (maxDiff < 10 || maxDiff / total < 0.1) { hide(); return; }
  el.innerHTML = `\u26a0 Market values here are uneven \u2014 an equal split puts up to <b>${fmt$(maxDiff)}</b> of basis on the wrong rows. ` +
    `<button type="button" class="underline font-semibold" onclick="purchDistSwitchMarket()">Switch to market-value split</button>`;
  el.classList.remove("hidden");
}

function purchDistSwitchMarket(): void {
  const sel = document.getElementById("purchDistMode") as HTMLSelectElement | null;
  if (sel) sel.value = "market";
  setPurchDistMode("market");
}

/** Update the (readonly) Unit Cost inputs + line totals in place \u2014 avoids a
 *  full items re-render, which would steal focus while the user is typing. */
function refreshDistCostInputs(): void {
  const s = S(); if (!s) return;
  const container = document.getElementById("purchaseItems"); if (!container) return;
  s.Items.forEach((it, i) => {
    if (isBulkItemType(it.Type)) return;
    const row = container.querySelector<HTMLElement>(`[data-purch-idx="${i}"]`);
    if (!row) return;
    const cost = row.querySelector<HTMLInputElement>("input[data-dist-cost]");
    if (cost && document.activeElement !== cost) cost.value = String(it.UnitCost ?? 0);
    const lt = row.querySelector<HTMLElement>("[data-line-total]");
    if (lt) lt.textContent = fmt$((Number(it.Qty) || 0) * (Number(it.UnitCost) || 0));
  });
}

/** Resolve catalog market prices for rows without a cached one, then re-apply.
 *  Only does work in "market" mode; conservative resolver (unique match only). */
let _distResolveSeq = 0;
async function resolveDistPrices(): Promise<void> {
  const s = S(); if (!s || !s._distOn) return;
  const seq = ++_distResolveSeq;
  const pending = distEligible(s.Items || []).filter(x => x._mktUnit === undefined && (x.Item || "").trim());
  if (pending.length) {
    const st = document.getElementById("purchDistStatus");
    if (st && s._distMode === "market") st.textContent = "Looking up market prices\u2026";
    await Promise.all(pending.map(async (it) => {
      try {
        const m = await resolveCatalogProduct({
          game: it.Game || "Pokemon", name: it.Item,
          number: it.Number || undefined, set: it.Set || undefined,
        });
        it._mktUnit = m ? ((await getCatalogPrice(m.productId))?.unit ?? null) : null;
      } catch { it._mktUnit = null; }
    }));
  }
  if (seq !== _distResolveSeq) return; // superseded by a newer resolve
  const cur = S(); if (!cur || !cur._distOn) return;
  if (cur._distMode === "market") {
    applyPurchaseDistribution();
    refreshDistCostInputs();
    recomputePurchaseTotals();
  }
  updateDistNudge();
}

let _distResolveTimer: number | undefined;
function scheduleDistResolve(): void {
  const s = S(); if (!s || !s._distOn) return;
  clearTimeout(_distResolveTimer);
  _distResolveTimer = window.setTimeout(() => { void resolveDistPrices(); }, 600);
}

function togglePurchDistribute(on: boolean): void {
  const s = S(); if (!s) return;
  s._distOn = on;
  document.getElementById("purchDistControls")?.classList.toggle("hidden", !on);
  if (on) {
    if (s._distTotal === undefined) {
      // Seed from whatever costs are already entered so toggling is non-destructive.
      const seed = distEligible(s.Items || []).reduce((a, x) => a + (Number(x.Qty) || 0) * (Number(x.UnitCost) || 0), 0);
      s._distTotal = +seed.toFixed(2);
      const inp = document.getElementById("purchDistTotal") as HTMLInputElement | null;
      if (inp) inp.value = s._distTotal ? String(s._distTotal) : "";
    }
    applyPurchaseDistribution();
    void resolveDistPrices();
  }
  renderPurchaseItemsList();
  markModalDirty();
}

function setPurchDistTotal(v: string): void {
  const s = S(); if (!s) return;
  s._distTotal = Number(v) || 0;
  applyPurchaseDistribution();
  refreshDistCostInputs();
  recomputePurchaseTotals();
  markModalDirty();
}

function setPurchDistMode(v: string): void {
  const s = S(); if (!s) return;
  s._distMode = v === "market" ? "market" : "equal";
  applyPurchaseDistribution();
  refreshDistCostInputs();
  recomputePurchaseTotals();
  if (s._distMode === "market") void resolveDistPrices();
  markModalDirty();
}

export async function savePurchase(origId: string): Promise<void> {
  const s = S(); if (!s) return;
  const formData = readForm($("#purchaseForm"));
  const purchaseId = (formData["Purchase ID"] || s["Purchase ID"] || nextId("PUR", "purchases", "Purchase ID")).trim();
  const date = formData["Date"] || todayISO();
  const vendor = formData["Vendor"] || "";
  const paymentMethod = formData["Payment Method"] || "";
  const fees = Number(formData["Fees"]) || 0;
  const notes = formData["Notes"] || "";

  // Receipt: read the hidden inputs; if a freshly-attached file is still a data
  // URL, push it to Storage now and keep the returned path.
  const receiptName = formData["Receipt File Name"] || "";
  const receiptType = formData["Receipt File Type"] || "";
  let receiptFile = formData["Receipt File"] || "";
  if (receiptFile && typeof receiptFile === "string" && receiptFile.startsWith("data:")) {
    try {
      toast("Uploading receipt\u2026");
      receiptFile = await uploadReceiptDataUrl(receiptFile, receiptName, receiptType);
    } catch (e: any) {
      toast("Receipt upload failed: " + (e?.message || e), "err");
      return;
    }
  }
  // On edit, evict a replaced/cleared previously-stored receipt file.
  if (origId) {
    const prevR = DB.table("purchases").find(r => r["Purchase ID"] === origId);
    const oldRef = prevR?.["Receipt File"];
    if (isStoragePath(oldRef) && oldRef !== receiptFile) deleteReceiptByPath(oldRef as string).catch(() => {});
  }

  // Bulk rows have no Item name — keep them when qty > 0. Singles/sealed still require a name.
  const items = (s.Items || []).filter(x =>
    isBulkItemType(x.Type) ? (Number(x.Qty) || 0) > 0 : (x.Item && x.Item.trim())
  );
  if (!items.length) { toast("Add at least one item.", "err"); return; }

  // Distribute-total mode: re-apply over the FILTERED items so removed/empty
  // rows can't hold a share of the total. Uses cached market prices (if any).
  if (s._distOn) {
    const distTotal = Number(s._distTotal) || 0;
    if (distTotal <= 0) { toast("Enter the total cost to distribute (or untick the distribute option).", "err"); return; }
    if (!distEligible(items).length) { toast("Distribute total cost is on, but there are no card/sealed rows to spread it across.", "err"); return; }
    applyPurchaseDistribution(items);
  }

  const subtotal = items.reduce((acc, x) => acc + (isBulkItemType(x.Type) ? (Number(x.UnitCost) || 0) : (Number(x.Qty) || 0) * (Number(x.UnitCost) || 0)), 0);
  const totalPaid = +(subtotal + fees).toFixed(2);
  const isNew = !origId;

  // Tally bulk additions for the toast summary.
  let newLotCount = 0;
  let bulkCardCount = 0;
  const bulkPoolsTouched = new Set<string>();

  if (isNew) {
    // Capitalize Fees / Tax / Shipping into the lots' basis, allocated across
    // items pro-rata by line value (bulk lines use their total cost). This is
    // the freight-in treatment: acquisition costs become inventory cost and
    // flow into COGS when items sell — they are NOT a separate expense, and
    // they are NOT lost from the P&L anymore (fixed 2026-07-11).
    const feeShareFor = (item: PurchaseItem): number => {
      if (fees <= 0) return 0;
      const lineValue = isBulkItemType(item.Type)
        ? (Number(item.UnitCost) || 0)
        : (Number(item.Qty) || 0) * (Number(item.UnitCost) || 0);
      if (subtotal > 0) return +((lineValue / subtotal) * fees).toFixed(2);
      return +(fees / Math.max(items.length, 1)).toFixed(2);
    };
    for (const it of items) {
      const qty = Number(it.Qty) || 1;
      const unitCost = Number(it.UnitCost) || 0;
      const feeShare = feeShareFor(it);
      const lotTotalBasis = +((unitCost * qty) + feeShare).toFixed(2);
      const lotUnitBasis = qty > 0 ? +(lotTotalBasis / qty).toFixed(2) : unitCost;
      if (isBulkItemType(it.Type)) {
        const klass = it.Type as BulkClass;
        addToBulkPool(klass, qty, (Number(it.UnitCost) || 0) + feeShare);
        it["Lot ID"] = bulkPoolLotId(klass);
        // Stamp identifying fields so the purchase line reads sensibly even though
        // bulk doesn't get its own per-purchase lot.
        if (!it.Item || !it.Item.trim()) it.Item = klass;
        bulkCardCount += qty;
        bulkPoolsTouched.add(klass);
      } else if (it.Type === "Sealed") {
        const newId = nextId("S", "sealed", "Lot ID");
        DB.add("sealed", {
          "Lot ID": newId, "Game": it.Game || "Pokemon",
          "Product": it.Item, "Set": normalizeSetName(it.Set), "Product Type": it.ProductType || "",
          "Inventory Class": "Long Term Hold", "Qty": qty,
          "Acq Date": date, "Acq Type": "Purchase", "Valuation Source": "Manual",
          "Unit Basis": lotUnitBasis, "Total Basis": lotTotalBasis,
          "Current FMV": +(unitCost * qty).toFixed(2), "Unrealized G/L": +((unitCost * qty) - lotTotalBasis).toFixed(2),
          "Source ID": purchaseId,
          "Notes": `From purchase ${purchaseId}${vendor ? ` · ${vendor}` : ""}`,
        });
        it["Lot ID"] = newId;
        newLotCount++;
      } else {
        const newId = nextId("C", "cards", "Card Lot ID");
        const isGraded = it.Type === "Graded";
        DB.add("cards", {
          "Card Lot ID": newId, "Game": it.Game || "Pokemon",
          "Card Name": it.Item, "Set": normalizeSetName(it.Set), "Card #": it.Number || "",
          "Inventory Class": isGraded ? "Slab" : "Raw",
          "Condition/Grade": isGraded ? (it.Grade || "PSA 10") : (it.Condition || "Near Mint"),
          "Qty": qty,
          "Acq Date": date, "Acq Type": "Purchase",
          "Valuation Source": (it.Game === "Magic: The Gathering" ? "Scryfall" : "TCGPlayer"),
          "Unit Basis": lotUnitBasis, "Total Basis": lotTotalBasis,
          "Current FMV": +(unitCost * qty).toFixed(2), "Unrealized G/L": +((unitCost * qty) - lotTotalBasis).toFixed(2),
          "Status": "Available", "Source ID": purchaseId,
          "Notes": `From purchase ${purchaseId}${vendor ? ` · ${vendor}` : ""}`,
        });
        it["Lot ID"] = newId;
        newLotCount++;
      }
    }
  }

  for (const it of items) delete it._mktUnit;

  const purchaseRow = {
    "Purchase ID": purchaseId, "Date": date,
    "Vendor": vendor, "Payment Method": paymentMethod,
    "Items": items, "Subtotal": +subtotal.toFixed(2),
    "Fees": fees, "Total Paid": totalPaid,
    "Notes": notes,
    "Receipt File": receiptFile, "Receipt File Name": receiptName, "Receipt File Type": receiptType,
  };
  // On edit, if the purchase Date changed, propagate it to the Acq Date of every
  // lot this purchase created (cards + sealed) so holding-period math stays
  // anchored to the real acquisition date. Bulk pools have no per-purchase lot,
  // so they're not affected.
  let datesSynced = 0;
  if (origId) {
    const prev = DB.table("purchases").find(r => r["Purchase ID"] === origId);
    const dateChanged = !!prev && fmtDate(prev["Date"]) !== fmtDate(date);
    DB.update("purchases", "Purchase ID", origId, purchaseRow);
    if (dateChanged) {
      DB.table("cards").forEach(c => {
        if (c["Source ID"] === purchaseId) { DB.update("cards", "Card Lot ID", c["Card Lot ID"], { "Acq Date": date }); datesSynced++; }
      });
      DB.table("sealed").forEach(sl => {
        if (sl["Source ID"] === purchaseId) { DB.update("sealed", "Lot ID", sl["Lot ID"], { "Acq Date": date }); datesSynced++; }
      });
    }
  } else {
    DB.add("purchases", purchaseRow);
  }

  // Optional embedded mileage trip — links to this purchase via Related Deal/Lot.
  await saveMileageSection(purchaseId, date);

  delete (window as any)._purchaseState;
  if (isNew) {
    const parts: string[] = [];
    if (newLotCount > 0) parts.push(`${newLotCount} lot(s) created`);
    if (bulkCardCount > 0) parts.push(`${fmtN(bulkCardCount)} bulk card(s) into ${bulkPoolsTouched.size} pool(s)`);
    toast(`Purchase saved · ${parts.join(" · ") || "no inventory changes"}`);
  } else {
    toast(datesSynced ? `Purchase updated · acquisition date synced to ${datesSynced} lot(s)` : "Purchase updated");
  }
  // New lots from purchase items (incl. CSV imports) may lack catalog identity:
  // link + image + price them server-side, then refresh.
  requestEnrichment();
  closeModal();
  render();
}

/* ---------------- Receipt upload/view/remove (in-form) ---------------- */
async function onPurchaseReceiptUpload(event: Event): Promise<void> {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  try {
    const result = await processReceiptFile(file);
    const form = document.getElementById("purchaseForm") as HTMLFormElement | null;
    if (!form) return;
    (form.querySelector('[name="Receipt File"]')      as HTMLInputElement).value = result.dataUrl;
    (form.querySelector('[name="Receipt File Name"]') as HTMLInputElement).value = result.name;
    (form.querySelector('[name="Receipt File Type"]') as HTMLInputElement).value = result.type;
    document.getElementById("purchaseReceiptUI")!.innerHTML = renderPurchaseReceiptUI({
      "Receipt File": result.dataUrl, "Receipt File Name": result.name, "Receipt File Type": result.type,
    });
    markModalDirty();
    toast("Receipt attached \u2014 click Save to commit");
  } catch (err: any) {
    toast("Upload failed: " + (err?.message || err), "err");
  }
}

function clearPurchaseReceipt(): void {
  const form = document.getElementById("purchaseForm") as HTMLFormElement | null;
  if (!form) return;
  (form.querySelector('[name="Receipt File"]')      as HTMLInputElement).value = "";
  (form.querySelector('[name="Receipt File Name"]') as HTMLInputElement).value = "";
  (form.querySelector('[name="Receipt File Type"]') as HTMLInputElement).value = "";
  document.getElementById("purchaseReceiptUI")!.innerHTML = renderPurchaseReceiptUI({});
  markModalDirty();
}

async function viewPurchaseReceiptFromForm(): Promise<void> {
  const form = document.getElementById("purchaseForm") as HTMLFormElement | null;
  if (!form) return;
  const fileRef = (form.querySelector('[name="Receipt File"]')      as HTMLInputElement).value;
  const type    = (form.querySelector('[name="Receipt File Type"]') as HTMLInputElement).value;
  const name    = (form.querySelector('[name="Receipt File Name"]') as HTMLInputElement).value;
  await viewReceipt(fileRef, type, name);
}

/** View the stored receipt for a purchase straight from the Purchases table. */
export async function viewReceiptForPurchase(id: string): Promise<void> {
  const row = DB.table("purchases").find(r => r["Purchase ID"] === id);
  if (!row || !row["Receipt File"]) { toast("No receipt attached", "err"); return; }
  await viewReceipt(row["Receipt File"], row["Receipt File Type"], row["Receipt File Name"]);
}

/** Pokémon TCG card lookup for a Raw/Graded purchase row. Fills name / set /
 *  number / game; leaves Unit Cost alone (that's what you paid, not market). */
export function lookupForPurchaseItem(idx: number): void {
  const s = S(); if (!s) return;
  const item = s.Items[idx];
  if (!item || isBulkItemType(item.Type) || item.Type === "Sealed") return;
  (window as any)._lookupOnPick = (c: any) => {
    item.Item   = c.name || "";
    item.Set    = normalizeSetName((c.set && c.set.name) || item.Set || "");
    item.Number = c.number || "";
    item.Game   = c.game || item.Game || "Pokemon";
    renderPurchaseItemsList(); markModalDirty();
    toast(`Picked: ${c.name}${c.set?.name ? " · " + c.set.name : ""}`);
    setTimeout(() => { delete (window as any)._lookupOnPick; }, 100);
  };
  const seed = (item.Item || "").trim();
  if (!seed) { toast("Type a card name (or 'name 123') first", "err"); return; }
  // Pass Card # and Set (when present) as a narrow context so the modal can
  // filter results down to the specific printing the user has in mind,
  // without making the API query so strict it returns zero.
  const narrow: { number?: string; set?: string; game?: string } = { game: item.Game || "Pokemon" };
  if (item.Number) narrow.number = item.Number.trim();
  if (item.Set)    narrow.set    = item.Set.trim();
  quickCardLookup(1, seed, narrow);
}

/** Sealed-catalog lookup for a Sealed purchase row. Fills product / set / type
 *  from the TCGCSV catalog; leaves Unit Cost alone. */
export function lookupSealedForPurchaseItem(idx: number): void {
  const s = S(); if (!s) return;
  const item = s.Items[idx];
  if (!item) return;
  (window as any)._sealedOnPick = (r: any) => {
    item.Item = r.name || "";
    item.Set  = normalizeSetName(r.group_name || item.Set || "");
    if (CATEGORY_TO_GAME[r.category_id]) item.Game = CATEGORY_TO_GAME[r.category_id];
    const ptMap: Record<string, string> = {
      box: "Booster Box", etb: "ETB", bundle: "Booster Bundle",
      pack: "Booster Pack", tin: "Tin", blister: "Blister", deck: "Deck",
      collection: "Collection Box", other: "Other",
    };
    if (r.product_kind && ptMap[r.product_kind]) item.ProductType = ptMap[r.product_kind];
    renderPurchaseItemsList(); markModalDirty();
    toast(`Picked: ${r.name}`);
    setTimeout(() => { delete (window as any)._sealedOnPick; }, 100);
  };
  openSealedPicker((item.Item || "").trim() || undefined);
}

// Inline-handler bridges
(window as any).editPurchase            = editPurchase;
(window as any).savePurchase            = savePurchase;
(window as any).addPurchaseItem         = addPurchaseItem;
(window as any).renderPurchaseItemsList = renderPurchaseItemsList;
(window as any).removePurchaseItem      = removePurchaseItem;
(window as any).updatePurchaseItemField = updatePurchaseItemField;
(window as any).togglePurchDistribute    = togglePurchDistribute;
(window as any).setPurchDistTotal        = setPurchDistTotal;
(window as any).setPurchDistMode         = setPurchDistMode;
(window as any).purchDistSwitchMarket    = purchDistSwitchMarket;
(window as any).lookupForPurchaseItem        = lookupForPurchaseItem;
(window as any).lookupSealedForPurchaseItem  = lookupSealedForPurchaseItem;
(window as any).onPurchaseReceiptUpload      = onPurchaseReceiptUpload;
(window as any).clearPurchaseReceipt         = clearPurchaseReceipt;
(window as any).viewPurchaseReceiptFromForm  = viewPurchaseReceiptFromForm;
(window as any).viewReceiptForPurchase       = viewReceiptForPurchase;
